import React, { useEffect, useState, useMemo } from 'react';
import { format, parse } from 'date-fns';
import { useQuery } from 'react-query';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import ResourceService, { ResourceProps } from 'services/hooks/resource-service';
import { Table } from '../Common/Table';
import Delete from './ResourceForms/delete';
import Save from './ResourceForms/save';
import variables from 'variables';
import { useNavigate } from 'react-router-dom';
import ManageIcon from 'assets/Icons/modals/ManageIcon';
import BlankImageIcon from 'assets/Icons/modals/BlankImageIcon';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import { FilterSelect } from 'utils/FilterSelectUtils';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';

export default function ResourceSaveMode() {
  /* This function can be used to programmatically navigate to different routes in the application. */
  const navigate = useNavigate();
  const [getResourcesResult, setGetResourcesResult] = useState<ResourceProps[] | null>([]);

  const {
    isLoading: isLoadingResources,
    refetch,
    data: resourceData
  } = useQuery<ResourceProps[], Error>('resources', ResourceService.findAllResources, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Amenities'));
    }
  });

  const { data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  useEffect(() => {
    if (resourceData && Array.isArray(resourceData)) {
      setGetResourcesResult(resourceData);
    }
  }, [resourceData]);

  const columns = useMemo(
    () => [
      {
        id: 'image',
        Header: 'Image',
        accessor: 'image',
        Cell: (cell: { value: string }) => {
          if (cell.value) {
            return (
              <img
                src={variables.RESOURCE_IMAGE_URL + cell.value}
                className="w-36 m-0 p-0 rounded-l-lg"
              ></img>
            );
          } else {
            return <BlankImageIcon />;
          }
        },
        width: 150,
        padding: 0,
        key: 'image',
        disableFilters: true
      },
      {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'building',
        Header: 'Building',
        accessor: (row: { buildingUuid: string }) => {
          return buildingsData?.find(
            (building: { uuid?: string }) => building?.uuid === row?.buildingUuid
          )?.name;
        },
        Filter: FilterSelect,
        disableSortBy: false
      },
      {
        id: 'Availability',
        Header: 'Availability',
        accessor: (row: { open: string; close: string }) =>
          row?.open &&
          row?.close &&
          `${format(parse(row?.open, 'HH:mm:ss', new Date()), 'p')} - ${format(
            parse(row?.close, 'HH:mm:ss', new Date()),
            'p'
          )}`,
        disableFilters: true
      },
      {
        id: 'notes',
        Header: 'Notes',
        accessor: 'description',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'access',
        Header: 'Access',
        accessor: (data: ResourceProps) => data,
        Cell: (row: { value: ResourceProps }) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => navigate(`/manage-resources/${row.value.uuid}`)}
                buttonLabel="Manage"
                variant="default"
                size="px-4 w-auto"
                iconLeft={<ManageIcon />}
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: ResourceProps) => data,
        Cell: (row: {
          value: ResourceProps;
          setEditUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setUpdateShowModal: React.Dispatch<React.SetStateAction<boolean>>;
          setDeleteUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setDeleteShowModal: React.Dispatch<React.SetStateAction<boolean>>;
        }) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    [buildingsData, navigate]
  );

  /* If the data is still loading or has not been fetched yet, the component returns a loading spinner component to indicate that the data is 
  being fetched. */
  if (isLoadingResources || !getResourcesResult) {
    return <FullScreenLoader />;
  }

  /* The code is conditionally rendering either the `Table` component or a `LoadingSpinner` component based on the values of `isLoadingResources` 
  and `getResourcesResult`. */
  return (
    <div className="resource">
      <Table
        name="Amenities"
        refetch={refetch}
        columns={columns}
        data={getResourcesResult}
        defaultSortBy={{ id: 'name', desc: false }}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={ResourceService.createResource}
        updateQuery={ResourceService.updateResource}
        deleteQuery={ResourceService.deleteResourceById}
        editQuery={ResourceService.findResourceById}
        queryName="resources"
        buttonName="Amenity"
        toolTipMessage="Add and manage bookable amenities."
      />
    </div>
  );
}
